/**
 * Content Block Utilities
 * 
 * Utility functions for working with content blocks across the application.
 * Includes functions for creating, validating, and transforming content blocks.
 */

/**
 * Creates a document reference content block
 * @param {Object} document - The document object from documentService
 * @param {string} connectionType - Type of connection ('primary' or 'related')
 * @returns {Object} Document reference content block
 */
export function createDocumentReferenceBlock(document, connectionType = 'related') {
  if (!document || !document.id) {
    throw new Error('Invalid document provided for content block creation');
  }

  return {
    type: 'document_reference',
    id: `doc_ref_${document.id}_${Date.now()}`,
    documentId: document.id,
    documentTitle: document.title || document.name || 'Untitled Document',
    documentType: document.primaryCategory || 'uncategorized',
    mimeType: document.mimeType || '',
    uploadedAt: document.uploadedAt || document.createdAt,
    fileSize: document.size,
    connectionType: connectionType,
    displayText: `📄 ${document.title || document.name || 'Document'}`,
    createdAt: new Date().toISOString(),
    metadata: {
      originalName: document.name,
      summary: document.summary,
      categories: document.categories
    }
  };
}

/**
 * Creates multiple document reference blocks from a list of documents
 * @param {Array} documents - Array of document objects
 * @param {string} connectionType - Type of connection for all documents
 * @returns {Array} Array of document reference content blocks
 */
export function createDocumentReferenceBlocks(documents, connectionType = 'related') {
  if (!Array.isArray(documents)) {
    return [];
  }

  return documents
    .filter(doc => doc && doc.id)
    .map(doc => createDocumentReferenceBlock(doc, connectionType));
}

/**
 * Validates a document reference content block
 * @param {Object} block - The content block to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function validateDocumentReferenceBlock(block) {
  if (!block || typeof block !== 'object') {
    return false;
  }

  const requiredFields = ['type', 'id', 'documentId', 'documentTitle'];
  return requiredFields.every(field => field in block && block[field]);
}

/**
 * Extracts document IDs from content blocks
 * @param {Array} contentBlocks - Array of content blocks
 * @returns {Array} Array of document IDs
 */
export function extractDocumentIds(contentBlocks) {
  if (!Array.isArray(contentBlocks)) {
    return [];
  }

  return contentBlocks
    .filter(block => block.type === 'document_reference' && block.documentId)
    .map(block => block.documentId);
}

/**
 * Removes document reference blocks with specific document IDs
 * @param {Array} contentBlocks - Array of content blocks
 * @param {Array} documentIds - Array of document IDs to remove
 * @returns {Array} Filtered array without the specified document references
 */
export function removeDocumentReferenceBlocks(contentBlocks, documentIds) {
  // Return empty array if contentBlocks is not an array
  if (!Array.isArray(contentBlocks)) {
    return [];
  }

  // Return original array if documentIds is not valid
  if (!Array.isArray(documentIds)) {
    return contentBlocks;
  }

  return contentBlocks.filter(block => {
    if (block.type !== 'document_reference') {
      return true; // Keep non-document blocks
    }
    return !documentIds.includes(block.documentId);
  });
}

/**
 * Updates document reference blocks with new document data
 * @param {Array} contentBlocks - Array of content blocks
 * @param {Object} updatedDocument - Updated document object
 * @returns {Array} Updated array of content blocks
 */
export function updateDocumentReferenceBlocks(contentBlocks, updatedDocument) {
  // Return empty array if contentBlocks is not an array
  if (!Array.isArray(contentBlocks)) {
    return [];
  }

  // Return original array if updatedDocument is invalid
  if (!updatedDocument || !updatedDocument.id) {
    return contentBlocks;
  }

  return contentBlocks.map(block => {
    if (block.type === 'document_reference' && block.documentId === updatedDocument.id) {
      // Create metadata object with only defined values
      const updatedMetadata = { ...block.metadata };

      // Only update summary if it's defined
      if (updatedDocument.summary !== undefined) {
        updatedMetadata.summary = updatedDocument.summary;
      }

      // Only update categories if it's defined
      if (updatedDocument.categories !== undefined) {
        updatedMetadata.categories = updatedDocument.categories;
      }

      // Update the block with new document information
      return {
        ...block,
        documentTitle: updatedDocument.title || updatedDocument.name || block.documentTitle,
        documentType: updatedDocument.primaryCategory || block.documentType,
        mimeType: updatedDocument.mimeType || block.mimeType,
        displayText: `📄 ${updatedDocument.title || updatedDocument.name || block.documentTitle || 'Document'}`,
        metadata: updatedMetadata
      };
    }
    return block;
  });
}

/**
 * Adds document reference blocks to existing content blocks
 * @param {Array} existingBlocks - Existing content blocks
 * @param {Array} newDocuments - New documents to add
 * @param {string} connectionType - Connection type for new documents
 * @returns {Array} Combined array of content blocks
 */
export function addDocumentReferenceBlocks(existingBlocks, newDocuments, connectionType = 'related') {
  const existing = existingBlocks || [];
  const newBlocks = createDocumentReferenceBlocks(newDocuments, connectionType);

  // Filter out duplicates based on documentId
  const existingDocIds = extractDocumentIds(existing);
  const uniqueNewBlocks = newBlocks.filter(block =>
    !existingDocIds.includes(block.documentId)
  );

  return [...existing, ...uniqueNewBlocks];
}

/**
 * Gets statistics about document references in content blocks
 * @param {Array} contentBlocks - Array of content blocks
 * @returns {Object} Statistics about document references
 */
export function getDocumentReferenceStats(contentBlocks) {
  if (!Array.isArray(contentBlocks)) {
    return { total: 0, byType: {}, byConnection: {} };
  }

  const docBlocks = contentBlocks.filter(block => block.type === 'document_reference');
  const stats = {
    total: docBlocks.length,
    byType: {},
    byConnection: {}
  };

  docBlocks.forEach(block => {
    // Count by document type
    const docType = block.documentType || 'uncategorized';
    stats.byType[docType] = (stats.byType[docType] || 0) + 1;

    // Count by connection type
    const connType = block.connectionType || 'related';
    stats.byConnection[connType] = (stats.byConnection[connType] || 0) + 1;
  });

  return stats;
}

/**
 * Entity Reference Content Block Utilities
 * Functions for creating and managing entity reference content blocks
 * for bidirectional linking between entities
 */

/**
 * Creates a day reference content block
 * @param {string} dayId - The ID of the day
 * @param {string} dayName - Display name for the day
 * @param {string} date - Date of the day (YYYY-MM-DD)
 * @param {Object} metadata - Optional metadata
 * @returns {Object} Day reference content block
 */
export function createDayReferenceBlock(dayId, dayName, date, metadata = {}) {
  if (!dayId || !dayName) {
    throw new Error('Day ID and name are required for day reference block');
  }

  return {
    type: 'day_reference',
    id: `day_ref_${dayId}_${Date.now()}`,
    dayId,
    dayName,
    date: date || null,
    displayText: `📅 ${dayName}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...metadata
  };
}

/**
 * Creates a place reference content block
 * @param {string} placeId - The ID of the place
 * @param {string} placeName - Display name for the place
 * @param {string} context - Optional context (e.g., 'visit', 'stay', 'transit')
 * @param {Object} metadata - Optional metadata
 * @returns {Object} Place reference content block
 */
export function createPlaceReferenceBlock(placeId, placeName, context = null, metadata = {}) {
  if (!placeId || !placeName) {
    throw new Error('Place ID and name are required for place reference block');
  }

  return {
    type: 'place_reference',
    id: `place_ref_${placeId}_${Date.now()}`,
    placeId,
    placeName,
    context: context || null,
    displayText: `📍 ${placeName}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...metadata
  };
}

/**
 * Creates a travel reference content block
 * @param {string} travelId - The ID of the travel item
 * @param {string} travelSummary - Display summary for the travel
 * @param {string} travelType - Type of travel (e.g., 'flight', 'train', 'bus')
 * @param {Object} metadata - Optional metadata
 * @returns {Object} Travel reference content block
 */
export function createTravelReferenceBlock(travelId, travelSummary, travelType = null, metadata = {}) {
  if (!travelId || !travelSummary) {
    throw new Error('Travel ID and summary are required for travel reference block');
  }

  const icon = travelType === 'flight' ? '✈️' : travelType === 'train' ? '🚂' : travelType === 'bus' ? '🚌' : travelType === 'car' ? '🚗' : '🚢';

  return {
    type: 'travel_reference',
    id: `travel_ref_${travelId}_${Date.now()}`,
    travelId,
    travelSummary,
    travelType: travelType || null,
    displayText: `${icon} ${travelSummary}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...metadata
  };
}

/**
 * Creates an accommodation reference content block
 * @param {string} accommodationId - The ID of the accommodation
 * @param {string} accommodationName - Display name for the accommodation
 * @param {string} context - Optional context (e.g., 'check-in', 'staying', 'check-out')
 * @param {Object} metadata - Optional metadata
 * @returns {Object} Accommodation reference content block
 */
export function createAccommodationReferenceBlock(accommodationId, accommodationName, context = null, metadata = {}) {
  if (!accommodationId || !accommodationName) {
    throw new Error('Accommodation ID and name are required for accommodation reference block');
  }

  return {
    type: 'accommodation_reference',
    id: `accommodation_ref_${accommodationId}_${Date.now()}`,
    accommodationId,
    accommodationName,
    context: context || null,
    displayText: `🏨 ${accommodationName}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...metadata
  };
}

/**
 * Generic function to create entity reference blocks
 * @param {string} entityType - Type of entity ('day', 'accommodation', 'place', 'travel', 'document')
 * @param {string} entityId - The ID of the entity
 * @param {string} entityName - Display name for the entity
 * @param {Object} metadata - Optional metadata specific to entity type
 * @returns {Object} Entity reference content block
 */
export function createEntityReferenceBlock(entityType, entityId, entityName, metadata = {}) {
  if (!entityType || !entityId || !entityName) {
    throw new Error('Entity type, ID, and name are required');
  }

  const validTypes = ['day', 'accommodation', 'place', 'travel', 'document'];
  if (!validTypes.includes(entityType)) {
    throw new Error(`Invalid entity type: ${entityType}. Must be one of: ${validTypes.join(', ')}`);
  }

  switch (entityType) {
  case 'day':
    return createDayReferenceBlock(entityId, entityName, metadata.date, metadata);
  case 'accommodation':
    return createAccommodationReferenceBlock(entityId, entityName, metadata.context, metadata);
  case 'place':
    return createPlaceReferenceBlock(entityId, entityName, metadata.context, metadata);
  case 'travel':
    return createTravelReferenceBlock(entityId, entityName, metadata.travelType, metadata);
  case 'document':
    return createDocumentReferenceBlock({ id: entityId, title: entityName, ...metadata }, metadata.connectionType || 'related');
  default:
    throw new Error(`Unsupported entity type: ${entityType}`);
  }
}

const TYPED_REFERENCE_BLOCK_TYPES = [
  'place_reference',
  'accommodation_reference',
  'travel_reference',
  'day_reference',
  'document_reference'
];

/**
 * Extracts cross-entity references from legacy content blocks for Related Entities display.
 * @param {Array} contentBlocks
 * @returns {Array<{ id?: string, entityType: string, entityId: string, entityName: string, blockType?: string }>}
 */
export function extractEntityReferencesFromContentBlocks(contentBlocks) {
  if (!Array.isArray(contentBlocks)) {
    return [];
  }

  return contentBlocks
    .map((block) => {
      if (!block || typeof block !== 'object') {
        return null;
      }

      let entityType = '';
      let entityId = '';
      let entityName = '';

      if (TYPED_REFERENCE_BLOCK_TYPES.includes(block.type)) {
        if (block.type === 'place_reference') {
          entityType = 'place';
          entityId = block.placeId;
          entityName = block.placeName;
        } else if (block.type === 'accommodation_reference') {
          entityType = 'accommodation';
          entityId = block.accommodationId;
          entityName = block.accommodationName || block.entityName;
        } else if (block.type === 'travel_reference') {
          entityType = 'travel';
          entityId = block.travelId;
          entityName = block.travelSummary || 'Travel';
        } else if (block.type === 'day_reference') {
          entityType = 'day';
          entityId = block.dayId;
          entityName = block.dayName || block.displayText?.replace(/^📅\s*/, '') || 'Day';
        } else if (block.type === 'document_reference') {
          entityType = 'document';
          entityId = block.documentId;
          entityName = block.documentTitle || 'Document';
        }
      } else if (block.sourceEntityType && block.sourceEntityId) {
        entityType = block.sourceEntityType;
        entityId = block.sourceEntityId;
        entityName = block.sourceEntityName;
      } else if (block.dayId && block.contentBlockId) {
        entityType = 'day';
        entityId = block.dayId;
        entityName = block.dayName
          || block.displayText?.replace(/^📅\s*/, '')
          || (block.date ? `Day (${block.date})` : 'Day');
      } else if (block.accommodationId) {
        entityType = 'accommodation';
        entityId = block.accommodationId;
        entityName = block.accommodationName || block.entityName || 'Accommodation';
      } else if (block.placeId) {
        entityType = 'place';
        entityId = block.placeId;
        entityName = block.placeName || block.entityName || 'Place';
      } else if (block.travelId) {
        entityType = 'travel';
        entityId = block.travelId;
        entityName = block.travelSummary || block.entityName || 'Travel';
      } else if (block.dayId) {
        entityType = 'day';
        entityId = block.dayId;
        entityName = block.dayName || block.displayText || 'Day';
      }

      if (!entityType || !entityId) {
        return null;
      }

      return {
        id: block.id,
        entityType,
        entityId,
        entityName: entityName || 'Unknown',
        blockType: block.type || block.sourceEntityType || 'legacy'
      };
    })
    .filter(Boolean);
}

/**
 * Checks if a content blocks array contains a reference to a specific entity
 * @param {Array} contentBlocks - Array of content blocks
 * @param {string} entityType - Type of entity to check for
 * @param {string} entityId - ID of the entity to check for
 * @returns {boolean} True if reference exists
 */
export function hasEntityReference(contentBlocks, entityType, entityId) {
  if (!Array.isArray(contentBlocks) || !entityType || !entityId) {
    return false;
  }

  const referenceTypeMap = {
    'day': 'day_reference',
    'accommodation': 'accommodation_reference',
    'place': 'place_reference',
    'travel': 'travel_reference',
    'document': 'document_reference'
  };

  const blockType = referenceTypeMap[entityType];
  if (!blockType) {
    return false;
  }

  return contentBlocks.some(block => {
    if (block.type !== blockType) {
      return false;
    }

    // Check entity-specific ID fields
    switch (entityType) {
    case 'day':
      return block.dayId === entityId;
    case 'accommodation':
      return block.accommodationId === entityId;
    case 'place':
      return block.placeId === entityId;
    case 'travel':
      return block.travelId === entityId;
    case 'document':
      return block.documentId === entityId;
    default:
      return false;
    }
  });
}

/**
 * Finds all content blocks that reference a specific entity
 * @param {Array} contentBlocks - Array of content blocks
 * @param {string} entityType - Type of entity to find
 * @param {string} entityId - ID of the entity to find
 * @returns {Array} Array of matching content blocks
 */
export function findEntityReferences(contentBlocks, entityType, entityId) {
  if (!Array.isArray(contentBlocks) || !entityType || !entityId) {
    return [];
  }

  const referenceTypeMap = {
    'day': 'day_reference',
    'accommodation': 'accommodation_reference',
    'place': 'place_reference',
    'travel': 'travel_reference',
    'document': 'document_reference'
  };

  const blockType = referenceTypeMap[entityType];
  if (!blockType) {
    return [];
  }

  return contentBlocks.filter(block => {
    if (block.type !== blockType) {
      return false;
    }

    // Check entity-specific ID fields
    switch (entityType) {
    case 'day':
      return block.dayId === entityId;
    case 'accommodation':
      return block.accommodationId === entityId;
    case 'place':
      return block.placeId === entityId;
    case 'travel':
      return block.travelId === entityId;
    case 'document':
      return block.documentId === entityId;
    default:
      return false;
    }
  });
}

/**
 * Removes content blocks that reference a specific entity
 * @param {Array} contentBlocks - Array of content blocks
 * @param {string} entityType - Type of entity to remove references for
 * @param {string} entityId - ID of the entity to remove references for
 * @returns {Array} Filtered array without the specified entity references
 */
export function removeEntityReference(contentBlocks, entityType, entityId) {
  if (!Array.isArray(contentBlocks)) {
    return [];
  }

  if (!entityType || !entityId) {
    return contentBlocks;
  }

  const referenceTypeMap = {
    'day': 'day_reference',
    'accommodation': 'accommodation_reference',
    'place': 'place_reference',
    'travel': 'travel_reference',
    'document': 'document_reference'
  };

  const blockType = referenceTypeMap[entityType];
  if (!blockType) {
    return contentBlocks;
  }

  return contentBlocks.filter(block => {
    // Also check for sourceEntityId format (used by Places content blocks)
    // These blocks use sourceEntityType + sourceEntityId instead of explicit reference types
    if (block.sourceEntityType === entityType && block.sourceEntityId === entityId) {
      return false; // Remove this block
    }

    if (block.type !== blockType) {
      return true; // Keep non-matching blocks
    }

    // Check entity-specific ID fields
    switch (entityType) {
    case 'day':
      return block.dayId !== entityId;
    case 'accommodation':
      return block.accommodationId !== entityId;
    case 'place':
      return block.placeId !== entityId;
    case 'travel':
      return block.travelId !== entityId;
    case 'document':
      return block.documentId !== entityId;
    default:
      return true;
    }
  });
}